import React, { useCallback, useState, useEffect } from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm, useDataRefresh} from '@xengage/gw-portals-viewmodel-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { EndorsementService } from 'gw-capability-policychange';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { MLProductCodeUtil, MLDisclosureUtil, MLEndorsementFlowUtil } from 'ml-portals-util-js';
import { platformMessages } from 'ml-components-platform-react';
import PolChangeTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PolChangeTrackEvent.jsx';
import { MLUWIssuesPanelComponent } from 'gw-capability-gateway-quoteandbind-common-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import appConfig from 'app-config';

import PolicyChangeSummaryComponent from '../../components/PolicyChangeSummaryComponent/PolicyChangeSummaryComponent';
import { Identify  } from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/TrackEvent';

import metadata from './PolicyChangeSummaryPage.metadata.json5';
import messages from './PolicyChangeSummaryPage.messages';

const {
    MLUWIssuesBlockingPoints,
} = appConfig;

const PolicyChangeSummaryPage = (props) => {
    const { wizardData, jumpTo, steps: currentSteps, currentStepIndex, changeNextSteps, goNext, currentStep, updateWizardData, history } = props;

    const { submissionVM: endorsementVM } = wizardData;

    const { authHeader } = useAuthentication();

    const currentStepName = currentStep.mlStepName;

    const [quoteVisible, setQuoteVisible] = useState(true);

    const isAgent = appConfig.portalCode === 'GPA';

    const productCode = _.get(endorsementVM, 'baseData.productCode.value', '');

    const [contingencies, setcontingencies] = useState(endorsementVM?.value?.preBindContingenciesToDTO || []);
    const [isFileUploadDone, setIsFileUploadDone] = useState(false);


    const {
        showProgressDialog,
    } = MLModalService();

    const { refreshData } = useDataRefresh();
    const [saveAndQuoted, updateSaveAndQuoted] = useState(false);

    useEffect(() => {
        Identify('Endorsement Step Reached', {step: 'Updated Premium Screen'}, endorsementVM, authHeader)
    }, [])

    useEffect(() => {
        if (MLNavigationStepsTrackingService.getData() && MLNavigationStepsTrackingService.getData().dynamicSteps.length < 1) {
            MLNavigationStepsTrackingService.retrieveFromPortalStorageEndorsement(endorsementVM.jobID.value, authHeader, refreshData);
        }

        const promise = EndorsementService.saveAndQuote(endorsementVM.value, authHeader);
        showProgressDialog(platformMessages.quotingPolicyChange, promise);
        promise.then((responseData) => {
            MLTagManagerService.sendFormStepPolicyChangeStep();
            endorsementVM.value = new PolicyChange(responseData);
            updateWizardData(wizardData);
            endorsementVM.value.portalCurrentStep_ML = currentStep.mlStepName;
            updateSaveAndQuoted(true);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isAgent) {
            PolChangeTrackEvent('Endorsement Step Reached', false, endorsementVM, authHeader, { step: 'PolChange Summary Screen' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const getBlockingUWIssues = useCallback(() => {
        return endorsementVM.value.uwIssues_ML && endorsementVM.value.uwIssues_ML.length
            ? endorsementVM.value.uwIssues_ML
                .filter((uwIssue) => uwIssue.currentBlockingPoint !== MLUWIssuesBlockingPoints.NON_BLOCKING)
            : [];
    }, [endorsementVM.value.uwIssues_ML]);

    const uwIssues = getBlockingUWIssues();

    const isQuoteBlockingUWIssue = (uwIssue) => {
        return uwIssue.currentBlockingPoint === MLUWIssuesBlockingPoints.BLOCKS_QUOTE;
    };

    const wasBlockingQuote = (uwIssue) => {
        return uwIssue.initialBlockingPoint_ML === MLUWIssuesBlockingPoints.BLOCKS_QUOTE;
    };

    const isRejectedUWIssue = (uwIssue) => {
        return uwIssue.hasApprovalOrRejection && uwIssue.currentBlockingPoint === MLUWIssuesBlockingPoints.REJECTED;
    };

    const isQuoteBlockedByUWIssues = () => {
        return uwIssues.some((uwIssue) => isQuoteBlockingUWIssue(uwIssue)
                                               || (wasBlockingQuote(uwIssue) && isRejectedUWIssue(uwIssue)));
    };

    const isQuoteBlockedByValidationErrors = () => {
        return endorsementVM.value.validationErrors_ML && endorsementVM.value.validationErrors_ML.length > 0;
    };

    const hasRejectedUWIssues = () => {
        return uwIssues.some((uwIssue) => isRejectedUWIssue(uwIssue));
    };

    const requoteEndorsement = useCallback(() => {
        const quotePromise = EndorsementService.quoteEndorsement([endorsementVM.jobID.value], authHeader);
        const modalMsg = messages.quotingPolicyChange;
        showProgressDialog(modalMsg, quotePromise);
        quotePromise.then((pc) => {
            endorsementVM.value = new PolicyChange(pc);
            setQuoteVisible(true);
        });
    }, [authHeader, endorsementVM, showProgressDialog]);

    const approvedAllQuoteBlockingUwIssuesCallback = useCallback(() => {
        requoteEndorsement();
    }, [requoteEndorsement]);

    const approvedAllBlockingUWIssuesCallback = useCallback(() => {
        endorsementVM.value.blockNavigation_ML = false;
    }, [endorsementVM.value]);

    const quoteBlockedByUWIssues = hasRejectedUWIssues() || isQuoteBlockedByUWIssues();
    const quoteBlockedByValidationErrors = isQuoteBlockedByValidationErrors();

    const getValidationErrorsOverrides = () => {
        const validationErrors = _.isEmpty(endorsementVM.value.validationErrors_ML) ? [] : endorsementVM.value.validationErrors_ML;
        if (!validationErrors) {
            return {};
        }
        
        const overrides = validationErrors.map((error, index) => {
            return {
                [`validationError${index}`]: {
                    message: error
                }
            }
        })
        return Object.assign({}, ...overrides);
    }

    
    //refresh Pre Bind
    const refreshPreBindContingencyData = () => {
        const loadPromise = EndorsementService.refreshPreBindContingencies(endorsementVM.policyNumber.value, authHeader);

        showProgressDialog(platformMessages.getPolicyDetails, loadPromise);
        loadPromise.then((contingencyData) => {
                if (contingencyData.length > 0) {
                    setcontingencies(contingencyData);
                    setIsFileUploadDone(true);
                } else {
                    setcontingencies([]);
                    setIsFileUploadDone(false);

                }
        });
    };

    const bindFlow = async () => {
        const canSkipSignatures = (MLNavigationStepsTrackingService.shouldQuickBind() && endorsementVM.baseData.signatureRequired_ML.value === false);
        if (canSkipSignatures) {
            const bindPromise = EndorsementService.bindEndorsement(endorsementVM.jobID.value, endorsementVM.paymentDetails_ML.value, authHeader);
            showProgressDialog(platformMessages.savingPolicyChange, bindPromise);
            bindPromise.then(({ policyChange, ...rest }) => {
                endorsementVM.value = new PolicyChange(policyChange, rest);
                updateWizardData(wizardData);
                MLTagManagerService.sendFormStepPolicyChangeStep();
                // go to endorsementConfirmation
                MLEndorsementFlowUtil.findStepAndChangeNextStep(productCode, 'endorsementConfirmation', changeNextSteps);
                goNext();
            },  (error) => {
                    const payrollAuthErrMessage = "pending payroll authorization contingency";

                    if(error.baseError.indexOf(payrollAuthErrMessage) > -1){
                        refreshPreBindContingencyData()
                    } else {
                        // Calling Promise.reject() will force the user to the ContactUs page.
                        Promise.reject(error);
                    }


                   
                });
            
        } else if (isAgent) {
            const uwPromise = EndorsementService.evaluateUWPrebind(endorsementVM.jobID.value, authHeader);
            showProgressDialog(platformMessages.savingPolicyChange, uwPromise);
            uwPromise.then((uwIssuesRes) => {
                endorsementVM.value.uwIssues_ML = uwIssuesRes;
                updateWizardData(wizardData);
                MLEndorsementFlowUtil.findStepAndChangeNextStep(productCode, 'endorsementSignatureOptions', changeNextSteps);
                goNext();
            });
        } else {
            // temporary commenting out this as userType should be other for CE
            // else if (submissionVM.baseData.userType_ML.value && submissionVM.baseData.userType_ML.value.code === 'other' && !isGpa) {

            // go to endorsementESignature
            MLEndorsementFlowUtil.findStepAndChangeNextStep(productCode, 'endorsementESignature', changeNextSteps);
            goNext();
        } 
    }

    const onNext = async () => {
        if (endorsementVM.lobData.homeowners && endorsementVM.lobData.homeowners.constructionDetails_ML && endorsementVM.lobData.homeowners.constructionDetails_ML.isDeviceRemovedAlert_ML) {
            endorsementVM.lobData.homeowners.constructionDetails_ML.isDeviceRemovedAlert_ML = false;
        }

        
        if (MLNavigationStepsTrackingService.shouldQuickBind()) { 
            bindFlow();
        } else if (MLProductCodeUtil.isPersonalUmbrella(productCode)) {
            if (MLDisclosureUtil.isRequotedAfterMvr(endorsementVM.jobID.value)) {
                MLDisclosureUtil.setRequoteAfterMvr(endorsementVM.jobID.value, false);
                const policyChangeResponse = await EndorsementService.saveEndorsement(
                    [endorsementVM.value],
                    authHeader
                );
                endorsementVM.value = new PolicyChange(policyChangeResponse);
                updateWizardData(wizardData);

                // go to bind flow
                bindFlow();
            } else {
                goNext();
            }
        } else if (MLProductCodeUtil.isHomeOwners(productCode)) {
            // go to bind flow as hoPostQUote just check on smart home validation
            bindFlow();
        } else if (MLProductCodeUtil.isPersonalAuto(productCode) && MLDisclosureUtil.isRequotedAfterMvr(endorsementVM.value.jobID)) {
            MLEndorsementFlowUtil.findStepAndChangeNextStep(productCode, 'endorsementPaVeracityCheck', changeNextSteps);
            goNext();
        } else {
            goNext();
        }

        if (isAgent) {
            PolChangeTrackEvent('Endorsement Step Submitted', false, endorsementVM, authHeader, {
                step: 'PolChange Summary Screen',
                action: 'Policy Change Summary Submitted'
            });
        }
    }

    const overrideProps = {
        pageContainer: {
            className: isAgent ? 'page-content-gpa clearfix' : 'clearfix'
        },
        uwIssuesPanel: {
            visible: isAgent,
            uwIssues,
            jobID: endorsementVM.jobID.value,
            rejectedIssuesAreQuoteBlocking: true,
            approvedAllQuoteBlockingUwIssuesCallback,
            approvedAllBlockingUWIssuesCallback,
            setQuoteVisible,
            submissionVM: endorsementVM,
            pendingContingencies: contingencies,
            key:contingencies,
            setIsFileUploadDone
        },
        validationErrorsIterable: {
            visible: quoteBlockedByValidationErrors,
            data: endorsementVM.value.validationErrors_ML
        },
        policyChangeSummaryComponent: {
            visible: quoteVisible,
            endorsementVM,
            jumpTo,
            currentSteps,
            currentStepIndex,
            changeNextSteps,
            history,
            goNext,
            onNext,
            currentStepName,
            saveAndQuoted,
            wizardData,
            updateWizardData,
            contingencies,
            refreshPreBindContingencyData,
            isFileUploadDone,
            key: contingencies
        },
        ...getValidationErrorsOverrides()
    }

    const resolvers = {
        resolveComponentMap: {
            MLUWIssuesPanelComponent,
            PolicyChangeSummaryComponent
        }
    }

    return (
        <WizardPage
            policyChangePageHeaderML={isAgent ? messages.makeChanges : ''}
            showCancel={false}
            showNext={false}
            showPrevious={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrideProps}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

PolicyChangeSummaryPage.propTypes = wizardProps;

export default PolicyChangeSummaryPage;